
import * as React from "react"
import { cn } from "@/lib/utils"
import { Label } from "./label"

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  hideLabel?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, helperText, hideLabel = false, id, ...props }, ref) => {
    // Generate unique ID if not provided and label exists
    const inputId = React.useMemo(
      () => id || (label ? `input-${Math.random().toString(36).substr(2, 9)}` : undefined),
      [id, label]
    );

    const inputElement = (
      <input
        id={inputId}
        type={type}
        className={cn(
          "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:border-muted md:text-sm",
          error && "border-destructive focus-visible:ring-destructive",
          className
        )}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={
          error || helperText ? `${inputId}-description` : undefined
        }
        aria-required={props.required}
        ref={ref}
        {...props}
      />
    );

    // If no label, return just the input (for backward compatibility)
    if (!label) {
      return inputElement;
    }

    // Return enhanced input with label and error/helper text
    return (
      <div className="w-full space-y-2">
        <Label
          htmlFor={inputId}
          className={cn(
            "text-sm font-medium",
            hideLabel && "sr-only"
          )}
        >
          {label}
          {props.required && (
            <span className="text-destructive ml-1" aria-label="required">
              *
            </span>
          )}
        </Label>
        {inputElement}
        {(error || helperText) && (
          <p
            id={`${inputId}-description`}
            className={cn(
              "text-sm",
              error ? "text-destructive" : "text-muted-foreground"
            )}
            role={error ? "alert" : undefined}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
)

Input.displayName = "Input"

export { Input }
